import { useEffect, useMemo } from "react";
import Head from "next/head";
import useSWR from "swr";

import useLocalStorage from "hooks/useLocalStorage";

import Nav from "@/components/Nav";
import { Alert } from "@catchy/ui";
import StopRow from "@/components/StopRow";

import { getCanonicalSiteUrl } from "@/lib/regions/client";
import { hasStoredStopId } from "@catchy/app-core";

const fetcher = (url) => fetch(url).then((res) => res.json());

const stops = [
  {
    stop_id: "9997",
    stop_name: "Queens Wharf",
  },
  {
    stop_id: "9998",
    stop_name: "Matiu/Somes Island Wharf",
  },
  {
    stop_id: "9999",
    stop_name: "Days Bay Wharf",
  },
];

export async function getStaticProps() {
  let alerts = [];

  try {
    const res = await fetch("https://catchy.nz/api/servicealerts/route-id/8");

    if (res.ok) {
      alerts = await res.json();
    } else {
      console.error("Failed to fetch ferry service alerts");
    }
  } catch (error) {
    console.error("Error fetching ferry service alerts:", error);
  }

  return { props: { alerts }, revalidate: 300 };
}

export default function FerryPage({ preferredStopNames, alerts }) {
  const siteUrl = getCanonicalSiteUrl();
  const { data: service_alerts } = useSWR(`/api/servicealerts/route-id/8`, {
    fetcher: fetcher,
    refreshInterval: 600000, // 10 minutes
    revalidateOnFocus: false,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    dedupingInterval: 600000,
    fallbackData: alerts,
  });

  const preferredStopNamesSSR = useMemo(
    () =>
      stops.map((e) => {
        const preferredName = preferredStopNames.find((el) =>
          hasStoredStopId(el, e.stop_id),
        )?.name;
        return { preferred_name: preferredName, ...e };
      }),
    [preferredStopNames],
  );
  const [lastMain, setLastMain] = useLocalStorage("last_main", null);

  useEffect(() => {
    setLastMain("ferry");
  }, [setLastMain]);

  const title =
    "Days Bay, Eastbourne and Matiu/Somes Island ferry times — Catchy";
  const description =
    "Find East by West ferry departures, live updates, wharf times, and timetables for Days Bay, Eastbourne, Matiu/Somes Island, and Queens Wharf.";
  const canonicalUrl = `${siteUrl}/ferry`;

  return (
    <>
      <Head>
        <title key="title">{title}</title>

        <meta name="twitter:text:title" content={title} key="twitterTitle" />
        <meta
          name="twitter:text:description"
          content={description}
          key="twitterDescription"
        />

        <meta property="og:title" content={title} key="ogTitle" />
        <meta
          property="og:description"
          content={description}
          key="ogDescription"
        />
        <meta name="description" content={description} key="description" />
        <meta
          property="og:image"
          content="https://catchy.nz/share/share-ferry.png"
          key="ogImage"
        />
        <meta property="og:url" content={canonicalUrl} key="ogUrl" />
        <link rel="canonical" href={canonicalUrl} key="canonicalUrl" />
      </Head>
      <Nav current="ferry" />
      <div className="mb-20 flex flex-col pt-8">
        <div className="mb-2 flex flex-col">
          <h1 className="mb-3 px-[20px] text-3xl font-semibold">Ferry Stops</h1>
          {preferredStopNamesSSR.map((element, key) => {
            return (
              <StopRow
                stop_name={
                  element.preferred_name
                    ? element.preferred_name
                    : element.stop_name
                }
                stop_id={element.stop_id}
                type="ferry"
                key={key}
              />
            );
          })}
        </div>

        {service_alerts?.length > 0 && (
          <div className="flex flex-col gap-y-3 py-4 sm:gap-y-4">
            {service_alerts.map((e, k) => (
              <Alert
                effect={e.effect}
                severity_level={e.severity_level}
                header_text={e.header_text}
                description_text={e.description_text}
                url={e.url}
                key={k}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
